/**
 * Module dependencies.
 */

import * as React from 'react';
import { cn } from 'app/lib/utils';

/**
 * `AvatarProps` type.
 */

type AvatarProps = {
  src?: string | null;
  initials: string;
  className?: string;
};

/**
 * Export `Avatar` component.
 *
 * Shows the profile photo when available, falling back to the initials badge
 * (and also falling back if the image fails to load).
 */

/**
 * A usable image URL is an absolute http(s) URL with a host, or a root-relative
 * path. This rejects junk values such as '', 'http://' or whitespace, which
 * would otherwise render as a broken image instead of the initials fallback.
 */

function isUsableUrl(src?: string | null): src is string {
  return typeof src === 'string' && /^(https?:\/\/[^\s]+|\/[^\s]+)/.test(src.trim());
}

function Avatar({ src, initials, className }: AvatarProps): JSX.Element {
  const [errored, setErrored] = React.useState(false);
  const showImage = isUsableUrl(src) && !errored;

  React.useEffect(() => {
    setErrored(false);
  }, [src]);

  return (
    <div
      className={cn(
        'relative grid shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-primary to-primary/60 font-bold text-primary-foreground',
        className
      )}
    >
      {showImage ? (
        <img
          alt={initials}
          className={'h-full w-full object-cover'}
          onError={() => setErrored(true)}
          referrerPolicy={'no-referrer'}
          src={(src as string).trim()}
        />
      ) : (
        <span>{initials}</span>
      )}
    </div>
  );
}

export { Avatar };
